import { RRule, Weekday } from "rrule";
import { getNumberWithOrdinal, toTitleCase } from "lib";
import { QUARTER_MAPPINGS, Suffix, TermType, weekdays } from "./constants";
import { isValidRRuleString } from "./validation";
import {
  MonthMapping,
  QuarterDayRuleResult,
  QuarterlyMappings,
  RRuleConfigProps,
  RRuleOptions,
} from "./types";

const createDefaultConfig = (): RRuleOptions => ({
  freq: RRule.DAILY,
  interval: undefined,
  bymonthday: undefined,
  byweekday: undefined,
  bymonth: undefined,
  count: undefined,
});

const weekdayValueRegex = /^([+-]?)(\d*)([A-Z]{2})$/;

export function getQuarterDayRule(dayOfQuarter: number): QuarterDayRuleResult {
  // Given a day of the quarter, return the bymonth and bymonthday for RRule.

  // Group by quarters and check thresholds
  const quarters: QuarterlyMappings[][] = [
    QUARTER_MAPPINGS.slice(0, 3), // Q1
    QUARTER_MAPPINGS.slice(3, 6), // Q2
    QUARTER_MAPPINGS.slice(6, 9), // Q3
    QUARTER_MAPPINGS.slice(9, 12), // Q4
  ];

  const quarterMappings: MonthMapping[] = quarters
    .map((quarter: QuarterlyMappings[]) => {
      // Find the FIRST month in this quarter where dayOfQuarter <= threshold
      const monthData = quarter.find(m => dayOfQuarter <= m.threshold);
      return monthData
        ? {
            month: monthData.month,
            day: dayOfQuarter - monthData.offset,
          }
        : null;
    })
    .filter((mapping): mapping is MonthMapping => mapping !== null);

  return {
    bymonth: quarterMappings.map(m => m.month),
    bymonthday: quarterMappings.map(m => m.day),
  };
}

export function getQuarterDayFromRule(
  bymonth: number | number[],
  bymonthday: number | number[],
): number[] {
  // Given the bymonth and bymonthday from an RRule, calculate the day of the quarter.
  const months: number[] = Array.isArray(bymonth) ? bymonth : [bymonth];
  const days: number[] = Array.isArray(bymonthday) ? bymonthday : [bymonthday];

  const quarterDays: number[] = months
    .map((month: number, i: number) => {
      const day = days[i] || days[0];
      const monthData = QUARTER_MAPPINGS.find(m => m.month === month);
      return monthData ? monthData.offset + day : null;
    })
    .filter((quarterDay): quarterDay is number => quarterDay !== null);

  const uniqueDays = [...new Set(quarterDays)];
  if (uniqueDays.length === 1) {
    return uniqueDays;
  }

  return quarterDays;
}

const parseRRuleToConfig = (rruleString: string) => {
  // Take our string and parse it into an RRule object and then into our config object for forms.
  // If the string is empty, return a default config
  if (!rruleString) {
    return createDefaultConfig();
  }

  const rule = RRule.fromString(rruleString);
  const { options } = rule;
  // RRule likes using undefined over null, and it makes the forms easier to work with.
  // We need to convert some of the options to strings for the form to work correctly.
  // Then RRule converts them back to what it needs when we create a new RRule string/object.

  // If we a have a bynweek day, we need to convert it to a string that we can use in the form.
  const newWeekdayString = options.bynweekday
    ? weekdays[options.bynweekday[0][0]]
        .nth(options.bynweekday[0][1])
        .toString()
    : undefined;

  const config = {
    bymonth: options.bymonth
      ? options.bymonth.map(month => month.toString()).join(",")
      : undefined,
    bymonthday: options.bymonthday,
    byweekday: options.bynweekday ? newWeekdayString : options.byweekday?.[0],
    freq: options.freq,
    interval: options.freq === RRule.DAILY ? options.interval : undefined,
    count: options.count || undefined,
  };

  if (options.freq === RRule.YEARLY) {
    // If the frequency is yearly, we need to handle the bymonthday and bymonth fields
    const quarterDay = getQuarterDayFromRule(
      options.bymonth,
      options.bymonthday,
    );
    config.bymonthday = quarterDay;
  }

  // If we have an options.bynmonthday with a value of -1, we need to set the byweekday to -1 to map it to our inputs
  if (options.freq === RRule.MONTHLY && options.bynmonthday[0] === -1) {
    config.byweekday = -1;
  }

  return config;
};
export const getFieldName = (base: string, suffix: string, field?: string) =>
  field ? `${base}${suffix}.${field}` : `${base}${suffix}`;

const processTermType = (term: RRuleConfigProps, termType: string) => {
  // Get the field names for the terms and config based on the term type
  const scheduleField = getFieldName(termType, Suffix.SCHEDULE_SUFFIX);
  const configField = getFieldName(termType, Suffix.CONFIG_SUFFIX);
  const parsedConfig = parseRRuleToConfig(term[scheduleField]);

  term[configField] = {
    ...(term[configField] || {}),
    ...parsedConfig,
  };
};

export const mapIncomingTerms = (term: RRuleConfigProps) => {
  // We need to get the rrule config from the terms string and then stuff it into the config field
  const mappedTerm = { ...term };
  processTermType(mappedTerm, TermType.BUYER);
  processTermType(mappedTerm, TermType.VENDOR);
  return mappedTerm;
};

export const getTermSummary = (
  tradingTerm: Record<string, any>,
  termType: string,
): string => {
  const schedule = tradingTerm[`${termType}${Suffix.SCHEDULE_SUFFIX}`];
  const terms = tradingTerm[`${termType}${Suffix.TERMS_SUFFIX}`];

  if (schedule && !isValidRRuleString(schedule)) {
    return "";
  }

  if (!schedule) {
    return `${terms} Days`;
  }

  const rule = RRule.fromString(
    tradingTerm[`${termType}${Suffix.SCHEDULE_SUFFIX}`],
  );
  const ruleString = rule.toText();
  let formatedRuleString = toTitleCase(ruleString);
  // if the feq is yearly, we need to just return every quarter on the x day of the quarter
  // If the count is in the rrule, it should be remove
  if (rule.options.freq === RRule.YEARLY) {
    const quarterDay = getQuarterDayFromRule(
      rule.options.bymonth,
      rule.options.bymonthday,
    );
    formatedRuleString = `Every Quarter On The ${getNumberWithOrdinal(quarterDay)} day`;
  }

  if (ruleString.includes("for 2 times")) {
    const parts = formatedRuleString.split(" ");
    parts.splice(1, 0, "Following");
    formatedRuleString = parts.join(" ").replace("For 2 Times", "");
  }

  return `Minimum ${terms} Days, Due: ${formatedRuleString}`;
};

export const parseWeekdayString = (
  weekdayValue: string | number,
): Weekday | number => {
  // Parses a string or number representing a weekday into an RRule Weekday constant or a number.
  // The input can be in the format of "MO", "TU", etc., or a number representing the weekday.
  // It can also include an optional sign and occurrence number, e.g., "+1MO" or "-2TU".

  const weekdayMap: Record<string, Weekday> = {
    MO: RRule.MO, // Monday
    TU: RRule.TU, // Tuesday
    WE: RRule.WE, // Wednesday
    TH: RRule.TH, // Thursday
    FR: RRule.FR, // Friday
    SA: RRule.SA, // Saturday
    SU: RRule.SU, // Sunday
  };

  if (typeof weekdayValue === "number") {
    // If the input is a number, just return it, it is already a weekday constant
    return weekdayValue;
  }

  // Regular expression to match the pattern: optional sign, number, weekday
  const match = weekdayValueRegex.exec(weekdayValue);

  if (!match) {
    // If the input does not match the expected format, default to 0 if the format is invalid
    return 0;
  }

  const [, sign, number, weekdayCode] = match;

  if (!(weekdayCode in weekdayMap)) {
    // Check if weekday code is valid
    return 0; // If not valid, default to 0
  }

  const weekdayConstant = weekdayMap[weekdayCode];

  // If no number is specified, return just the weekday constant
  if (!number) {
    return weekdayConstant;
  }

  // Parse the occurrence number
  let occurrence = parseInt(number, 10);

  // Apply sign if present
  if (sign === "-") {
    occurrence = -occurrence;
  } // Positive sign or no sign: keep as is

  return weekdayConstant.nth(occurrence);
};
